import React, { useCallback, useEffect, useState } from "react";
import CustomModal from "../../components/common/customModal";
import { Box } from "@mui/system";
import {
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { BudgetData, RewardsRecognitionDataData } from "../../types/dashboard";
import { useLocation } from "react-router-dom";
import dashboardService from "../../services/DashboardServices";
import { showToast } from "../../components/common/toastNotification";
import { useAppSelector } from "../../globalState/hooks";
import { LoginRootState } from "../../types/login";

interface RewardAcceptanceFormProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  isLoading?: boolean;
  rewardAcceptanceFormData: RewardsRecognitionDataData;
}

const RewardAcceptanceForm: React.FC<RewardAcceptanceFormProps> = ({
  open,
  onClose,
  onSave,
  isLoading,
  rewardAcceptanceFormData,
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idFromParams = searchParams.get("RewardAndRecognitionId");
  const [loadingButton, setLoadingButton] = useState<
    "approve" | "reject" | null
  >(null);
  const [comment, setComment] = useState<string>(
    rewardAcceptanceFormData?.comment || ""
  );
  const [commentError, setCommentError] = useState<boolean>(false);
  const [budgetData, setBudgetData] = useState<BudgetData | null>(null);
  const userDetails = useAppSelector(
    (state: LoginRootState) => state.authReducer.loginDetials
  );

  const handleApproveReject = async (status: number) => {
    if (!idFromParams) return;
    if (status === 3 && !comment.trim()) {
      setCommentError(true);

      return;
    }
    const selectedButton = status === 2 ? "approve" : "reject";
    setLoadingButton(selectedButton);

    const inputData = {
      id: Number(idFromParams),
      status: status,
      comment: comment,
    };

    try {
      const response = await dashboardService.RewardAndRecoApproveReject(
        inputData
      );
      const finalResponse = response.data;

      if (finalResponse.success) {
        showToast(finalResponse.message, "success");
        onClose();
        setComment("");
        setCommentError(false);
      } else {
        showToast(finalResponse.message, "error");
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setLoadingButton(null);
    }
  };
  const handleClose = () => {
    setComment("");
    onClose();
    setCommentError(false);
  };
  const handelGetBudgetDataofCXOorHead = useCallback(async () => {
    const EmpId = userDetails?.userId;

    try {
      const response = await dashboardService.getBudgetDataofCXOorHead(EmpId);
      if (response?.data?.success && response.data.data) {
        setBudgetData(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch budget data:", error);
    }
  }, [userDetails?.userId]);
  useEffect(() => {
    handelGetBudgetDataofCXOorHead();
  }, [handelGetBudgetDataofCXOorHead]);
  return (
    <CustomModal
      borderBottom="0px"
      open={open}
      isSaveButtonShow={false}
      width={700}
      handleClose={handleClose}
      title={"Reward Acceptance Form"}
      onSave={onSave}
    >
      <Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {isLoading ? (
            <Box textAlign="center" py={4}>
              <CircularProgress style={{ color: "#FFFFFF" }} />
            </Box>
          ) : (
            <Box sx={{ width: "100%" }}>
              <Box mb={2}>
                <Box display="flex" alignItems="flex-start">
                  <Typography
                    variant="body2"
                    color="#B0B0B0"
                    sx={{ minWidth: 190, pr: 2, pt: "2px" }}
                  >
                    Reward Type:
                  </Typography>
                  <Typography variant="body1" color="#FFFFFF" fontWeight={500}>
                    {rewardAcceptanceFormData?.awardType || "-"}
                    {rewardAcceptanceFormData.crossFunctionalType &&
                    rewardAcceptanceFormData.crossFunctionalType !== "0"
                      ? ` (${rewardAcceptanceFormData.crossFunctionalType})`
                      : ""}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1, borderColor: "#555" }} />
              </Box>
              {/* Type */}
              <Box mb={2}>
                <Box display="flex" alignItems="flex-start">
                  <Typography
                    variant="body2"
                    color="#B0B0B0"
                    sx={{ minWidth: 190, pr: 2, pt: "2px" }}
                  >
                    Type:
                  </Typography>
                  <Typography variant="body1" color="#FFFFFF" fontWeight={500}>
                    {rewardAcceptanceFormData?.awardId === 1 ||
                    rewardAcceptanceFormData?.awardId === 2
                      ? rewardAcceptanceFormData?.categoryType || "-"
                      : rewardAcceptanceFormData?.awardId === 3
                      ? rewardAcceptanceFormData?.crossFunctionalType || "-"
                      : "-"}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1, borderColor: "#555" }} />
              </Box>

              {/* Nominate Employee */}
              <Box mb={2}>
                <Box display="flex" alignItems="flex-start">
                  <Typography
                    variant="body2"
                    color="#B0B0B0"
                    sx={{ minWidth: 190, pr: 2, pt: "2px" }}
                  >
                    Nominated Employee:
                  </Typography>
                  <Box>
                    {rewardAcceptanceFormData
                      ?.lstRewardAndRecognizationNomination_Members?.length >
                    0 ? (
                      rewardAcceptanceFormData.lstRewardAndRecognizationNomination_Members.map(
                        (member, index) => (
                          <Typography
                            key={index}
                            variant="body1"
                            color="#FFFFFF"
                            fontWeight={500}
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {member.employeeName}
                          </Typography>
                        )
                      )
                    ) : (
                      <Typography variant="body1" color="#FFFFFF">
                        -
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Divider sx={{ my: 1, borderColor: "#555" }} />
              </Box>

              {/* Score */}
              <Box mb={2}>
                <Box display="flex" alignItems="flex-start">
                  <Typography
                    variant="body2"
                    color="#B0B0B0"
                    sx={{ minWidth: 190, pr: 2, pt: "2px" }}
                  >
                    Score:
                  </Typography>
                  <Typography variant="body1" color="#FFFFFF" fontWeight={500}>
                    {rewardAcceptanceFormData?.score?.toString() || "-"}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1, borderColor: "#555" }} />
              </Box>

              {/* Achievement Details */}
              <Box mb={2}>
                <Box display="flex" alignItems="flex-start">
                  <Typography
                    variant="body2"
                    color="#B0B0B0"
                    sx={{ minWidth: 190, pr: 2, pt: "2px" }}
                  >
                    Achievement Details:
                  </Typography>
                  <Typography
                    variant="body1"
                    color="#FFFFFF"
                    fontWeight={500}
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {rewardAcceptanceFormData?.achievementDetails || "-"}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1, borderColor: "#555" }} />
              </Box>
              {/* comment  */}

              <Box mb={2}>
                <Box display="flex" alignItems="flex-start">
                  <Typography
                    variant="body2"
                    color="#B0B0B0"
                    sx={{ minWidth: 190, pr: 2, pt: "2px" }}
                  >
                    Comments:
                  </Typography>
                  <Box sx={{ width: "100%" }}>
                    {rewardAcceptanceFormData.status === 1 ? (
                      <TextField
                        multiline
                        minRows={3}
                        maxRows={5}
                        fullWidth
                        placeholder="Enter comment"
                        value={comment}
                        onChange={(e) => {
                          setComment(e.target.value);
                        }}
                        error={commentError}
                        helperText={
                          commentError
                            ? "Comment is required when rejecting."
                            : ""
                        }
                        sx={{
                          "& .MuiInputBase-root": {
                            color: "#FFFFFF",
                            backgroundColor: "#2c2c2c",
                            border: "1px solid",
                            borderColor: commentError ? "#f44336" : "#555",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: commentError ? "#f44336" : "#555",
                            },
                            "&:hover fieldset": {
                              borderColor: commentError ? "#f44336" : "#555",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: commentError ? "#f44336" : "#555",
                            },
                          },
                          "& .MuiInputLabel-root, & .MuiFormHelperText-root": {
                            color: "#B0B0B0",
                          },
                        }}
                      />
                    ) : (
                      <Typography
                        variant="body1"
                        color="#FFFFFF"
                        fontWeight={500}
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {rewardAcceptanceFormData?.comment || "-"}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Divider sx={{ my: 1, borderColor: "#555" }} />
              </Box>

              <Box mb={2}>
                <Box display="flex" alignItems="flex-start">
                  <Typography
                    variant="body2"
                    color="#B0B0B0"
                    sx={{ minWidth: 190, pr: 2, pt: "2px" }}
                  >
                    Actual Budget
                  </Typography>
                  <Typography
                    variant="body1"
                    color="#FFFFFF"
                    fontWeight={500}
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {rewardAcceptanceFormData?.awardType ===
                    "Cross Functional Award"
                      ? budgetData?.allocatedCrossFunctionalBudget
                      : budgetData?.allocatedIndividualBudget}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1, borderColor: "#555" }} />
              </Box>
              <Box mb={2}>
                <Box display="flex" alignItems="flex-start">
                  <Typography
                    variant="body2"
                    color="#B0B0B0"
                    sx={{ minWidth: 190, pr: 2, pt: "2px" }}
                  >
                    Utilized Budget
                  </Typography>
                  <Typography
                    variant="body1"
                    color="#FFFFFF"
                    fontWeight={500}
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {rewardAcceptanceFormData?.awardType ===
                    "Cross Functional Award"
                      ? budgetData?.usedCrossFunctionalBudget
                      : budgetData?.usedIndividualBudget}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1, borderColor: "#555" }} />
              </Box>
              <Box mb={2}>
                <Box display="flex" alignItems="flex-start">
                  <Typography
                    variant="body2"
                    color="#B0B0B0"
                    sx={{ minWidth: 190, pr: 2, pt: "2px" }}
                  >
                    Available Budget
                  </Typography>
                  <Typography
                    variant="body1"
                    color="#FFFFFF"
                    fontWeight={500}
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {rewardAcceptanceFormData?.awardType ===
                    "Cross Functional Award"
                      ? budgetData?.availableCrossFunctionalBudget
                      : budgetData?.availableIndividualBudget}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1, borderColor: "#555" }} />
              </Box>
            </Box>
          )}
        </Box>

        {/* Buttons */}
        {rewardAcceptanceFormData.status === 1 && (
          <Box display="flex" justifyContent="center" gap={2} mt={3}>
            <Button
              fullWidth
              variant="contained"
              className="saveBtn"
              style={{
                borderRadius: "0px",
                background: "#393939",
                maxWidth: "140px",
                marginLeft: "20px",
              }}
              onClick={() => handleApproveReject(2)}
            >
              {loadingButton === "approve" ? (
                <CircularProgress size={20} style={{ color: "#fff" }} />
              ) : (
                <Typography>Approve</Typography>
              )}
            </Button>
            <Button
              fullWidth
              variant="contained"
              className="saveBtn"
              style={{
                borderRadius: "0px",
                background: "#393939",
                maxWidth: "140px",
                marginLeft: "20px",
              }}
              onClick={() => handleApproveReject(3)}
            >
              {loadingButton === "reject" ? (
                <CircularProgress size={20} style={{ color: "#fff" }} />
              ) : (
                <Typography>Reject</Typography>
              )}
            </Button>
          </Box>
        )}
      </Box>
    </CustomModal>
  );
};

export default RewardAcceptanceForm;
